import type { TypeSummary } from "./types";

// Returns a short, customer-facing summary for an EquipmentCard. Uses
// the server-provided `type.summary` when it reads like real copy; falls
// back to a derived line composed from the type's attributes when the
// summary is null, empty, or clearly seed-data.
//
// The audit flagged the staging filler ("Staging fixture — Bobcat S70
// Mini Skid Steer.") as making the cards read like a database export.
// Until photo/copy curation lands we keep the customer surface honest
// by substituting a real attribute-derived line.

const SEED_DATA_PATTERNS: readonly RegExp[] = [
  /^staging fixture/i,
  /^generated by/i,
  /^lorem /i,
  /^placeholder/i,
];

export function isSeedDataSummary(summary: string | null | undefined): boolean {
  if (summary == null) return true;
  const trimmed = summary.trim();
  if (trimmed.length === 0) return true;
  return SEED_DATA_PATTERNS.some((re) => re.test(trimmed));
}

export function deriveCardSummary(type: TypeSummary): string {
  const bits: string[] = [];

  // Lead with a delivery signal when available — concrete dollar value
  // beats a generic "insured fleet" claim. Don't restate the weekly
  // rate; it's rendered below the summary.
  if (type.delivery_fee_cents > 0) {
    bits.push(`Delivered for $${Math.round(type.delivery_fee_cents / 100).toLocaleString("en-US")}`);
  } else if (type.from_price_per_week_cents != null && type.from_price_per_week_cents > 0) {
    bits.push("Insured fleet, jobsite-ready");
  } else {
    bits.push("Quote-only — call for pricing");
  }

  if (type.requires_coi) {
    bits.push("Bring your own COI or add the waiver");
  } else {
    bits.push("Damage waiver included");
  }
  return bits.join(" · ");
}

export function effectiveCardSummary(type: TypeSummary): string {
  if (!isSeedDataSummary(type.summary)) return type.summary as string;
  return deriveCardSummary(type);
}
